import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentUserTypeValue, InvalidAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {InvalidMessagingUserType, ValidMessagingUserType} from './MessagingUserType';
import {UserTypeClaimsUnion, UserTypeClaimKey} from '../Claims';
import {InvalidQuotingUserType, ValidQuotingUserType} from './QuotingUserType';

export type InvalidUserTypeValue = InvalidAgentUserType | InvalidMessagingUserType | InvalidQuotingUserType;

export type ValidUserTypeValue = ValidAgentUserType | ValidMessagingUserType | ValidQuotingUserType;

type UserTypeValue = InvalidUserType['value'] | ValidUserType['value'];

const isValidUserType = (userTypeValue: UserTypeValue): userTypeValue is ValidUserTypeValue =>
  userTypeValue.key === 'ValidAgentUserType' ||
  userTypeValue.key === 'ValidMessagingUserType' ||
  userTypeValue.key === 'ValidQuotingUserType';

export class InvalidUserType {
  public readonly key = 'InvalidUserType';

  constructor(public readonly value: InvalidUserTypeValue) {}
}

export class ValidUserType {
  public static create(userTypeValue: UserTypeValue): UserType {
    return isValidUserType(userTypeValue) ? new ValidUserType(userTypeValue) : new InvalidUserType(userTypeValue);
  }

  public readonly key = 'ValidUserType';
  private constructor(public readonly value: ValidUserTypeValue) {}
}

export type UserType = ValidUserType | InvalidUserType;

export type UserTypeOptions<TReturn> = ToMatchOptions<UserType, 'key', TReturn>;

export const areAllUserTypesValid = (userTypes: UserType[]): userTypes is ValidUserType[] =>
  userTypes.every(ut => ut.key === 'ValidUserType');

export const AllowedExternalUserTypes = ['ValidAgentUserType'];
export const AllowedExternalAgentUserTypes = ['ValidAgentUserType', 'ValidAgentSupportUserType'];
export const isAgentUserTypesAllowedForExternal = (agentUserType: AgentUserTypeValue) =>
  AllowedExternalAgentUserTypes.includes(agentUserType.key);
export const areAllUserTypesAllowedForExternal = (userTypes: ValidUserType[]) =>
  userTypes.every(
    ut =>
      AllowedExternalUserTypes.includes(ut.value.key) &&
      (ut.value.key !== 'ValidAgentUserType' || isAgentUserTypesAllowedForExternal(ut.value.value))
  );

export const matchUserType = <TReturn>(userType: UserType, options: UserTypeOptions<TReturn>): TReturn => {
  switch (userType.key) {
    case 'InvalidUserType':
      return options.InvalidUserType(userType);
    case 'ValidUserType':
      return options.ValidUserType(userType);
  }
};

type ExhaustiveUserTypeOptions<TReturn> = {
  Agent: (agentUserType: InvalidAgentUserType | ValidAgentUserType) => TReturn;
  Messaging?: () => TReturn;
  Quoting: (quotingUserType: InvalidQuotingUserType | ValidQuotingUserType) => TReturn;
};

type UserTypeOptionsWithDefault<TReturn> = {
  Agent?: (agentUserType: InvalidAgentUserType | ValidAgentUserType) => TReturn;
  Quoting?: (quotingUserType: InvalidQuotingUserType | ValidQuotingUserType) => TReturn;
  Messaging?: () => TReturn;
  Default: (userType: InvalidUserType['value'] | ValidUserType['value']) => TReturn;
};

export type LineOfBusinessUserTypeOptions<TReturn> =
  | ExhaustiveUserTypeOptions<TReturn>
  | UserTypeOptionsWithDefault<TReturn>;

export const matchUserTypeByLineOfBusiness = <TReturn>(
  userType: InvalidUserType['value'] | ValidUserType['value'],
  options: LineOfBusinessUserTypeOptions<TReturn>
): TReturn => {
  switch (userType.key) {
    case 'ValidAgentUserType':
    case 'InvalidAgentUserType':
      return options.Agent ? options.Agent(userType) : (options as any).Default!(userType);
    case 'ValidQuotingUserType':
    case 'InvalidQuotingUserType':
      return options.Quoting ? options.Quoting(userType) : (options as any).Default!(userType);
    default:
      return (options as any).Default ? (options as any).Default(userType) : userType;
  }
};

const negate = <TInput>(predicate: (value: TInput) => boolean) => (value: TInput) => !predicate(value);

export const isAgentUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidAgentUserType' || userType.key === 'InvalidAgentUserType';
export const isMessagingUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidMessagingUserType' || userType.key === 'InvalidMessagingUserType';
export const isQuotingUserType = (userType: UserTypeValue) =>
  userType.key === 'ValidQuotingUserType' || userType.key === 'InvalidQuotingUserType';

export const isNotAgentUserType = negate(isAgentUserType);
export const isNotMessagingUserType = negate(isMessagingUserType);

type ExhaustiveValidUserTypeOptions<TReturn> = {
  Agent: (agentUserType: ValidAgentUserType) => TReturn;
  Quoting: (quotingUserType: ValidQuotingUserType) => TReturn;
  Messaging: (messagingUserType: ValidMessagingUserType) => TReturn;
};

type ValidUserTypeOptionsWithDefault<TReturn> = {
  Agent?: (agentUserType: ValidAgentUserType) => TReturn;
  Quoting?: (quotingUserType: ValidQuotingUserType) => TReturn;
  Messaging?: (messagingUserType: ValidMessagingUserType) => TReturn;
  Default: (userType: ValidUserType['value']) => TReturn;
};

export type LineOfBusinessValidUserTypeOptions<TReturn> =
  | ExhaustiveValidUserTypeOptions<TReturn>
  | ValidUserTypeOptionsWithDefault<TReturn>;

export const matchValidUserTypeByLineOfBusiness = <TReturn>(
  userType: ValidUserType['value'],
  options: LineOfBusinessValidUserTypeOptions<TReturn>
): TReturn => {
  switch (userType.key) {
    case 'ValidAgentUserType':
      return options.Agent ? options.Agent(userType) : (options as any).Default!(userType);
    case 'ValidMessagingUserType':
      return options.Messaging ? options.Messaging(userType) : (options as any).Default!(userType);
    case 'ValidQuotingUserType':
      return options.Quoting ? options.Quoting(userType) : (options as any).Default!(userType);
    default:
      return (options as any).Default ? (options as any).Default(userType) : userType;
  }
};

export type UserTypeClaimKeyOptions<TReturn> = ToMatchOptions<UserTypeClaimsUnion, '$type', TReturn>;
export const matchUserTypeClaimKey = <TReturn>(
  userClaim: UserTypeClaimsUnion,
  options: UserTypeClaimKeyOptions<TReturn>
): TReturn => {
  switch (userClaim.$type) {
    case UserTypeClaimKey.AgentSite:
      return options['Renaissance.Authorization.Claims.AgentClaim.AgentUserTypeClaim, Renaissance.Authorization'](
        userClaim
      );
    case UserTypeClaimKey.MyAccessPortal:
      return options['Renaissance.Authorization.Claims.PortalClaim.PortalUserTypeClaim, Renaissance.Authorization'](
        userClaim
      );
    case UserTypeClaimKey.Quoting:
      return options['Renaissance.Authorization.Claims.QuotingClaim.QuotingUserTypeClaim, Renaissance.Authorization'](
        userClaim
      );
    default:
      return options.Empty(userClaim);
  }
};
